import { ReactNode } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import CenteredModalDialog from "@/components/blocks/CenteredModalDialog";
import { FormContainer } from "./FormContainer";
import { FormHeader } from "./FormHeader";
import { FormBody } from "./FormBody";
import { FormActions } from "./FormActions";
import { Button } from "./Button";

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  onSubmit?: (e: React.FormEvent) => void;
  className?: string;
  showCloseButton?: boolean;
}

export function FormModal({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  onSubmit,
  className = "max-w-xl",
  showCloseButton = true,
}: FormModalProps) {
  return (
    <CenteredModalDialog show={open} onClose={onClose} className={className}>
      <FormContainer className="h-full overflow-hidden">
        <form onSubmit={onSubmit} className="flex h-full flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="relative">
              <FormHeader title={title} description={description} />
              {showCloseButton && (
                <div className="absolute right-4 top-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground h-auto w-auto p-1"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close modal</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>

            <FormBody>{children}</FormBody>
          </div>

          {actions && <FormActions>{actions}</FormActions>}
        </form>
      </FormContainer>
    </CenteredModalDialog>
  );
}

interface FormModalActionsProps {
  onCancel: () => void;
  submitText?: string;
  cancelText?: string;
  isLoading?: boolean;
  submitDisabled?: boolean;
}

export function FormModalActions({
  onCancel,
  submitText = "Save",
  cancelText = "Cancel",
  isLoading = false,
  submitDisabled = false,
}: FormModalActionsProps) {
  return (
    <>
      <Button type="button" variant="outline" onClick={onCancel}>
        {cancelText}
      </Button>
      <Button type="submit" loading={isLoading} disabled={submitDisabled || isLoading}>
        {submitText}
      </Button>
    </>
  );
}
